import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

export interface Device {
  id: string
  name: string
  device_identifier: string
  status: string
  timezone?: string
  device_type?: string
  phone_number?: string
  vehicle_type?: string
  plate_number?: string
  owner_name?: string
  trip_idle_threshold_min?: number
  tracking_schedule?: TrackingSchedule
  last_seen_at?: string | null
  created_at: string
}

export const DEVICE_TYPES = ['gps_tracker', 'vehicle_obd', 'asset_tracker', 'personal'] as const
export const VEHICLE_TYPES = ['car', 'truck', 'van', 'motorcycle', 'bus', 'heavy_equipment', 'other'] as const

export interface TrackingSchedule {
  mode: 'always' | 'scheduled'
  days?: number[]       // 0=Sun, 1=Mon, ..., 6=Sat
  start_time?: string   // "HH:MM"
  end_time?: string     // "HH:MM"
}

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
export const DEFAULT_SCHEDULE: TrackingSchedule = { mode: 'always' }

export interface SensorDefinition {
  id: string
  key: string
  name: string
  unit: string
  data_type: string
  created_at: string
}

export function useDevices() {
  return useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get('/devices').then((r) => unwrapList<Device>(r.data)),
  })
}

export function useDevice(id: string) {
  return useQuery({
    queryKey: ['devices', id],
    queryFn: () => api.get<Device>(`/devices/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; device_identifier: string }) =>
      api.post<Device>('/devices', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  })
}

export function useUpdateDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Device> & { id: string }) =>
      api.put<Device>(`/devices/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  })
}

export function useDeleteDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  })
}

export function useSensorDefinitions() {
  return useQuery({
    queryKey: ['sensor-definitions'],
    queryFn: () => api.get('/sensor-definitions').then((r) => unwrapList<SensorDefinition>(r.data)),
  })
}

export function useCreateSensorDefinition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { key: string; name: string; unit: string; data_type: string }) =>
      api.post<SensorDefinition>('/sensor-definitions', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sensor-definitions'] }),
  })
}

export function useDeviceSensors(deviceId: string) {
  return useQuery({
    queryKey: ['device-sensors', deviceId],
    queryFn: () =>
      api.get(`/devices/${deviceId}/sensors`).then((r) => unwrapList<SensorDefinition>(r.data)),
    enabled: !!deviceId,
  })
}

// ── Device assignments ────────────────────────────────────────────

export interface DeviceAssignment {
  id: string
  device_id: string
  user_id: string
  assigned_at: string
  unassigned_at: string | null
}

export function useDeviceAssignments(deviceId: string) {
  return useQuery({
    queryKey: ['device-assignments', deviceId],
    queryFn: () =>
      api.get(`/devices/${deviceId}/assignments`).then((r) => unwrapList<DeviceAssignment>(r.data)),
    enabled: !!deviceId,
  })
}

export function useAssignDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, userId }: { deviceId: string; userId: string }) =>
      api.post<DeviceAssignment>(`/devices/${deviceId}/assignments`, { user_id: userId }).then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['device-assignments', vars.deviceId] })
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
  })
}

export function useUnassignDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, assignmentId }: { deviceId: string; assignmentId: string }) =>
      api.delete(`/devices/${deviceId}/assignments/${assignmentId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['device-assignments', vars.deviceId] })
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
  })
}
